import { Directive, ElementRef, inject, NgZone, OnDestroy, OnInit, output, OutputEmitterRef } from '@angular/core';
import { WindowRefService } from '@services/window-ref-service';

const KEYBOARD_OPEN_THRESHOLD_PX = 120;
// iOS Safari overlays its form accessory and address bar without shrinking visualViewport.
const KEYBOARD_CTA_CLEARANCE_PX = 96;
const VIEWPORT_SETTLE_MS = 50;
const FOCUS_SCROLL_DELAY_MS = 300;
const FOCUS_MEASURE_RECHECK_MS = 350;
const FIELD_CTA_GAP_PX = 12;
const CTA_SELECTOR = '[data-keyboard-cta]';

/**
 * Publishes the on-screen keyboard height as `--cureskin-keyboard-inset` on the host element, and
 * keeps the focused input scrolled clear of the keyboard.
 *
 * Not a duplicate of `KeyboardVisibilityDirective`. See the "Keyboard handling" section of
 * `client/app/onboarding/onboarding-profile/CLAUDE.md` before changing this or adding a third.
 */
@Directive({
  selector: '[appKeyboardInset]',
})
export class KeyboardInsetDirective implements OnInit, OnDestroy {
  /** Fires only on transitions, so bindings settle instead of thrashing per animation frame. */
  readonly keyboardOpenChange: OutputEmitterRef<boolean> = output<boolean>();

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly windowRef: WindowRefService = inject(WindowRefService);
  private readonly zone: NgZone = inject(NgZone);

  private keyboardOpen: boolean = false;
  private keyboardInset: number = 0;
  private viewport: VisualViewport | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private focusScrollTimer: ReturnType<typeof setTimeout> | null = null;
  private paddedContainer: HTMLElement | null = null;
  private paddedContainerOriginalPadding: string = '';
  private readonly onViewportChange = (): void => this.scheduleMeasure();
  private readonly onFocusIn = (event: FocusEvent): void => this.handleFocusIn(event);

  ngOnInit(): void {
    const nativeWindow = this.windowRef.nativeWindow;
    this.viewport = nativeWindow?.visualViewport ?? null;
    if (!this.viewport) return;

    this.zone.runOutsideAngular((): void => {
      this.viewport?.addEventListener('resize', this.onViewportChange);
      this.viewport?.addEventListener('scroll', this.onViewportChange);
      this.elementRef.nativeElement.addEventListener('focusin', this.onFocusIn);
    });
    this.measure();
  }

  ngOnDestroy(): void {
    this.viewport?.removeEventListener('resize', this.onViewportChange);
    this.viewport?.removeEventListener('scroll', this.onViewportChange);
    this.elementRef.nativeElement.removeEventListener('focusin', this.onFocusIn);
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.focusScrollTimer) clearTimeout(this.focusScrollTimer);
    this.releaseScrollRoom();
    this.setProperty('--cureskin-keyboard-inset', 0);
  }

  private scheduleMeasure(): void {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout((): void => this.measure(), VIEWPORT_SETTLE_MS);
  }

  private measure(): void {
    const nativeWindow = this.windowRef.nativeWindow;
    if (!this.viewport || !nativeWindow) return;
    const hiddenHeight = nativeWindow.innerHeight - (this.viewport.height + this.viewport.offsetTop);
    const isOpen = hiddenHeight > KEYBOARD_OPEN_THRESHOLD_PX;
    const ctaClearance = this.elementRef.nativeElement.hasAttribute('data-keyboard-cta-clearance')
      ? KEYBOARD_CTA_CLEARANCE_PX
      : 0;
    this.keyboardInset = isOpen ? Math.round(hiddenHeight + ctaClearance) : 0;
    this.setProperty('--cureskin-keyboard-inset', this.keyboardInset);
    if (isOpen === this.keyboardOpen) return;
    this.keyboardOpen = isOpen;
    if (!isOpen) this.releaseScrollRoom();
    this.zone.run((): void => this.keyboardOpenChange.emit(isOpen));
  }

  private measureCta(): number {
    const cta = this.elementRef.nativeElement.querySelector<HTMLElement>(CTA_SELECTOR);
    return cta ? Math.round(cta.getBoundingClientRect().height) : 0;
  }

  private setProperty(name: string, pixels: number): void {
    this.elementRef.nativeElement.style.setProperty(name, `${pixels}px`);
  }

  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || !(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (this.focusScrollTimer) clearTimeout(this.focusScrollTimer);
    this.focusScrollTimer = setTimeout((): void => {
      this.measure();
      this.revealField(target);
      if (!this.elementRef.nativeElement.hasAttribute('data-keyboard-cta-clearance')) return;
      this.focusScrollTimer = setTimeout((): void => {
        this.measure();
        this.focusScrollTimer = null;
      }, FOCUS_MEASURE_RECHECK_MS);
    }, FOCUS_SCROLL_DELAY_MS);
  }

  private revealField(target: HTMLElement): void {
    if (!this.viewport) return;
    this.releaseScrollRoom();
    const hiddenBy = this.hiddenBelowCta(target);
    if (hiddenBy <= 0) return;
    const hostScroller = this.findScrollContainer(target, this.elementRef.nativeElement.parentElement);
    const scroller = hostScroller ?? this.findScrollContainer(target, null);
    if (!scroller) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    const scrollableRemaining = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
    if (scrollableRemaining < hiddenBy) {
      if (!hostScroller) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      this.reserveScrollRoom(scroller, hiddenBy - scrollableRemaining);
    }
    scroller.scrollBy({ top: hiddenBy, behavior: 'smooth' });
  }

  private hiddenBelowCta(target: HTMLElement): number {
    if (!this.viewport) return 0;
    const safeBottom = this.viewport.offsetTop + this.viewport.height - this.measureCta() - FIELD_CTA_GAP_PX;
    return Math.round(target.getBoundingClientRect().bottom - safeBottom);
  }

  private reserveScrollRoom(container: HTMLElement, extraPixels: number): void {
    this.paddedContainer = container;
    this.paddedContainerOriginalPadding = container.style.paddingBottom;
    const basePadding = parseFloat(
      this.windowRef.nativeWindow?.getComputedStyle(container)?.paddingBottom ?? '0',
    ) || 0;
    container.style.paddingBottom = `${Math.round(basePadding + extraPixels)}px`;
  }

  private releaseScrollRoom(): void {
    if (!this.paddedContainer) return;
    this.paddedContainer.style.paddingBottom = this.paddedContainerOriginalPadding;
    this.paddedContainer = null;
    this.paddedContainerOriginalPadding = '';
  }

  private findScrollContainer(target: HTMLElement, boundary: HTMLElement | null): HTMLElement | null {
    let node: HTMLElement | null = target.parentElement;
    while (node && node !== boundary) {
      const overflowY = this.windowRef.nativeWindow?.getComputedStyle(node)?.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return node;
      node = node.parentElement;
    }
    return null;
  }
}
